/**
 * Profitability screen data seam (docs/profitability.md E.3; slice P7 `web-profit`). Every read goes through the
 * generated SDK + `unwrap()`, so a failed lookup throws and is never shown as an empty list or a zero (P11).
 *
 * The URL owns the filter bar (`ProfitSearch`): a view can be shared by copying the address. `toApiFilters` turns it
 * into the API's `ProfitFilters` query (flat query params, `from_month`/`to_month` as YYYY-MM).
 */
import { queryOptions } from '@tanstack/react-query'
import { unwrap } from '../../lib/api/client'
import {
  addDeskAgentRateApiV1ProfitDeskAgentsDeskAgentIdRatesPost,
  type ContractorsView,
  type ContractsView,
  type CustomerDetail,
  type CustomerLinkIn,
  type CustomerList,
  closePeriodApiV1ProfitPeriodsMonthClosePost,
  contractorsApiV1ProfitContractorsGet,
  contractsApiV1ProfitContractsGet,
  customerDetailApiV1ProfitCustomersCustomerIdGet,
  customersApiV1ProfitCustomersGet,
  type DataQuality,
  type DeskAgentOut,
  type DeskAgentPatch,
  type DeskAgentRateIn,
  type DeskPnl,
  dataQualityApiV1ProfitDataQualityGet,
  deskAgentsApiV1ProfitDeskAgentsGet,
  dismissFindingApiV1ProfitLeakageFindingIdDismissPost,
  type FactPage,
  type FindingOut,
  factsApiV1ProfitFactsGet,
  type LeakageView,
  leakageApiV1ProfitLeakageGet,
  linkCustomerApiV1ProfitCustomerLinksPost,
  listProjectsApiV1ProjectsGet,
  lockPeriodApiV1ProfitPeriodsMonthLockPost,
  metaApiV1ProfitMetaGet,
  type PageProjectRow,
  type PeriodOut,
  type ProfitFilters,
  type ProfitMeta,
  patchDeskAgentApiV1ProfitDeskAgentsDeskAgentIdPatch,
  pnlApiV1ProfitPnlGet,
  reopenPeriodApiV1ProfitPeriodsMonthReopenPost,
  type TechsView,
  techsApiV1ProfitTechsGet,
} from '../../lib/api/generated'

export const PROFIT_TABS = ['pnl', 'customers', 'contracts', 'techs', 'contractors', 'projects', 'leakage', 'quality'] as const
export type ProfitTab = (typeof PROFIT_TABS)[number]
export const PERIOD_KINDS = ['month', 'quarter', 'ytd', 'custom'] as const
export type PeriodKind = (typeof PERIOD_KINDS)[number]
export const QUICK_FILTERS = ['underwater', 'at_risk', 'check_attribution', 'placeholder_rates'] as const
export type QuickFilter = (typeof QUICK_FILTERS)[number]
export const LEAKAGE_STATES = ['open', 'dismissed', 'resolved', 'all'] as const
export type LeakageState = (typeof LEAKAGE_STATES)[number]

/** Roles that may open the screen at all (the API's COST_READERS). Cover and tech never see it. */
export const PROFIT_ROLES = new Set(['manager', 'billing'])
export const canSeeProfit = (role: string | null | undefined): boolean => Boolean(role && PROFIT_ROLES.has(role))

/** Portfolio rows for the Projects tab. Facts only (`GET /projects`), never a live Books read. */
export const projectsProfitQuery = () =>
  queryOptions({
    queryKey: ['profit', 'projects'] as const,
    queryFn: ({ signal }): Promise<PageProjectRow> => unwrap(listProjectsApiV1ProjectsGet({ query: { lane: 'all', limit: 200 }, signal })),
  })

/** The URL search state. Defaults are omitted so a plain `/profit` is "this month, everything". */
export interface ProfitSearch {
  tab?: ProfitTab
  period?: PeriodKind
  /** Anchor month (YYYY-MM) for month / quarter / YTD. */
  month?: string
  /** Custom range, YYYY-MM. */
  from?: string
  to?: string
  basis?: 'close'
  org?: 'ewee' | 'smartpbx'
  customer?: string
  /** Display label for the chosen customer (so a shared link reads well without another lookup). */
  customer_name?: string
  children?: boolean
  kind?: string
  domain?: 'phone' | 'it' | 'macd'
  tech?: string
  contractor?: string
  dept?: string
  ticket_kind?: string
  quotable?: boolean
  /** Customers tab quick filter. */
  quick?: QuickFilter
  /** Customer drawer open on this customer id. */
  open?: string
  /** Leakage worklist state. */
  lstate?: Exclude<LeakageState, 'open'>
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function text(value: unknown, max = 120): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : undefined
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}

function flag(value: unknown): true | undefined {
  return value === true || value === 'true' || value === '1' ? true : undefined
}

function month(value: unknown): string | undefined {
  const v = text(value, 7)
  return v && MONTH.test(v) ? v : undefined
}

function uuid(value: unknown): string | undefined {
  const v = text(value, 36)
  return v && UUID.test(v) ? v : undefined
}

/** validateSearch for `/profit`: anything unknown or malformed is dropped, never guessed. */
export function parseProfitSearch(raw: Record<string, unknown>): ProfitSearch {
  const out: ProfitSearch = {
    tab: oneOf(raw.tab, PROFIT_TABS),
    period: oneOf(raw.period, PERIOD_KINDS),
    month: month(raw.month),
    from: month(raw.from),
    to: month(raw.to),
    basis: raw.basis === 'close' ? 'close' : undefined,
    org: oneOf(raw.org, ['ewee', 'smartpbx'] as const),
    customer: uuid(raw.customer),
    customer_name: text(raw.customer_name),
    children: flag(raw.children),
    kind: text(raw.kind, 60),
    domain: oneOf(raw.domain, ['phone', 'it', 'macd'] as const),
    tech: uuid(raw.tech),
    contractor: uuid(raw.contractor),
    dept: text(raw.dept, 60),
    ticket_kind: text(raw.ticket_kind, 60),
    quotable: flag(raw.quotable),
    quick: oneOf(raw.quick, QUICK_FILTERS),
    open: uuid(raw.open),
    lstate: oneOf(raw.lstate, ['dismissed', 'resolved', 'all'] as const),
  }
  if (out.tab === 'pnl') out.tab = undefined
  if (out.period === 'month') out.period = undefined
  if (!out.customer) {
    out.customer_name = undefined
    out.children = undefined
  }
  for (const key of Object.keys(out) as (keyof ProfitSearch)[]) if (out[key] === undefined) delete out[key]
  return out
}

export function monthKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function shiftMonth(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number) as [number, number]
  const d = new Date(y, m - 1 + delta, 1)
  return monthKey(d)
}

/** The month range a search names, as `[from, to]` (YYYY-MM, inclusive). */
export function periodRange(search: ProfitSearch, now: Date = new Date()): [string, string] {
  const anchor = search.month ?? monthKey(now)
  const kind = search.period ?? 'month'
  if (kind === 'quarter') {
    const [y, m] = anchor.split('-').map(Number) as [number, number]
    const start = Math.floor((m - 1) / 3) * 3 + 1
    const from = `${y}-${String(start).padStart(2, '0')}`
    return [from, shiftMonth(from, 2)]
  }
  if (kind === 'ytd') return [`${anchor.slice(0, 4)}-01`, anchor]
  if (kind === 'custom') {
    const from = search.from ?? anchor
    const to = search.to ?? from
    return from <= to ? [from, to] : [to, from]
  }
  return [anchor, anchor]
}

/** The API's ProfitFilters for a search. `user_id` is sent only for a manager (the API ignores it for billing anyway). */
export function toApiFilters(search: ProfitSearch, opts: { manager: boolean; now?: Date }): ProfitFilters {
  const [from, to] = periodRange(search, opts.now)
  const f: ProfitFilters = { from_month: from, to_month: to, basis: search.basis ?? 'transaction', org: search.org ?? 'both' }
  if (search.customer) {
    f.customer_id = search.customer
    if (search.children) f.include_children = true
  }
  if (search.kind) f.contract_kind = search.kind
  if (search.domain) f.domain = search.domain
  if (opts.manager && search.tech) f.user_id = search.tech
  if (search.contractor) f.contractor_id = search.contractor
  if (search.dept) f.department = search.dept
  if (search.ticket_kind) f.ticket_kind = search.ticket_kind
  if (search.quotable) f.quotable_only = true
  return f
}

/** Flat query string for a link (CSV export). Same keys as the API's ProfitFilters. */
export function filterParams(filters: ProfitFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  return params
}

export type ExportView = 'customers' | 'contracts' | 'contractors' | 'leakage' | 'facts' | 'techs'

export function exportCsvHref(view: ExportView, filters: ProfitFilters): string {
  const params = filterParams(filters)
  params.set('view', view)
  return `/api/v1/profit/export.csv?${params}`
}

// --- queries ---------------------------------------------------------------------------------------------------------

/**
 * `/profit/facts`, `/profit/leakage` and `/profit/export.csv` declare `filters` as one object query param in the
 * generated contract (FastAPI did not flatten the model next to other params). The API reads them flat like every
 * other profit GET, so the screen sends them flat and casts.
 */
// biome-ignore lint/suspicious/noExplicitAny: see above; the generated query type for these three routes is wrong.
type FlatQuery = any

const STALE = 60_000

export const profitMetaQuery = () =>
  queryOptions({
    queryKey: ['profit', 'meta'],
    queryFn: ({ signal }): Promise<ProfitMeta> => unwrap(metaApiV1ProfitMetaGet({ signal })),
    staleTime: STALE,
  })

export const pnlQuery = (filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'pnl', filters],
    queryFn: ({ signal }): Promise<DeskPnl> => unwrap(pnlApiV1ProfitPnlGet({ query: filters, signal })),
    staleTime: STALE,
  })

export const customersQuery = (filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'customers', filters],
    queryFn: ({ signal }): Promise<CustomerList> => unwrap(customersApiV1ProfitCustomersGet({ query: filters, signal })),
    staleTime: STALE,
  })

export const customerDetailQuery = (customerId: string, filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'customer', customerId, filters],
    queryFn: ({ signal }): Promise<CustomerDetail> =>
      unwrap(customerDetailApiV1ProfitCustomersCustomerIdGet({ path: { customer_id: customerId }, query: filters, signal })),
    staleTime: STALE,
  })

export const contractsQuery = (filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'contracts', filters],
    queryFn: ({ signal }): Promise<ContractsView> => unwrap(contractsApiV1ProfitContractsGet({ query: filters, signal })),
    staleTime: STALE,
  })

export const techsQuery = (filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'techs', filters],
    queryFn: ({ signal }): Promise<TechsView> => unwrap(techsApiV1ProfitTechsGet({ query: filters, signal })),
    staleTime: STALE,
  })

export const contractorsQuery = (filters: ProfitFilters) =>
  queryOptions({
    queryKey: ['profit', 'contractors', filters],
    queryFn: ({ signal }): Promise<ContractorsView> => unwrap(contractorsApiV1ProfitContractorsGet({ query: filters, signal })),
    staleTime: STALE,
  })

export const leakageQuery = (filters: ProfitFilters, state: LeakageState = 'open') =>
  queryOptions({
    queryKey: ['profit', 'leakage', state, filters],
    queryFn: ({ signal }): Promise<LeakageView> =>
      unwrap(leakageApiV1ProfitLeakageGet({ query: { ...filters, state } as FlatQuery, signal })),
    staleTime: STALE,
  })

export interface FactsQuery {
  service_line?: string
  kind?: string[]
  treatment?: string
  month?: string
  contract_id?: string
  case_id?: string
  limit?: number
}

export const factsQuery = (filters: ProfitFilters, extra: FactsQuery) =>
  queryOptions({
    queryKey: ['profit', 'facts', filters, extra],
    queryFn: ({ signal }): Promise<FactPage> =>
      unwrap(factsApiV1ProfitFactsGet({ query: { ...filters, limit: 50, ...extra } as FlatQuery, signal })),
    staleTime: STALE,
  })

export const dataQualityQuery = () =>
  queryOptions({
    queryKey: ['profit', 'data-quality'],
    queryFn: ({ signal }): Promise<DataQuality> => unwrap(dataQualityApiV1ProfitDataQualityGet({ signal })),
    staleTime: STALE,
  })

export const deskAgentsQuery = (enabled: boolean) =>
  queryOptions({
    queryKey: ['profit', 'desk-agents'],
    queryFn: ({ signal }): Promise<DeskAgentOut[]> => unwrap(deskAgentsApiV1ProfitDeskAgentsGet({ signal })),
    staleTime: STALE,
    enabled,
  })

// --- writes (manager unless noted; the API enforces the role) --------------------------------------------------------

export type PeriodAction = 'close' | 'lock' | 'reopen'

/** `month` is a PeriodOut.month date (YYYY-MM-01); the path takes YYYY-MM. */
export function periodPath(monthDate: string): string {
  return monthDate.slice(0, 7)
}

export function periodAction(action: PeriodAction, monthDate: string, note?: string): Promise<PeriodOut> {
  const opts = { path: { month: periodPath(monthDate) }, body: { note: note?.trim() || null } }
  if (action === 'close') return unwrap(closePeriodApiV1ProfitPeriodsMonthClosePost(opts))
  if (action === 'lock') return unwrap(lockPeriodApiV1ProfitPeriodsMonthLockPost(opts))
  return unwrap(reopenPeriodApiV1ProfitPeriodsMonthReopenPost(opts))
}

/** Billing and manager may dismiss; the reason is required (3+ characters) and audited. */
export function dismissFinding(findingId: string, reason: string): Promise<FindingOut> {
  return unwrap(dismissFindingApiV1ProfitLeakageFindingIdDismissPost({ path: { finding_id: findingId }, body: { reason } }))
}

export function linkBooksCustomer(body: CustomerLinkIn): Promise<unknown> {
  return unwrap(linkCustomerApiV1ProfitCustomerLinksPost({ body }))
}

export function setDeskAgentRate(deskAgentId: string, body: DeskAgentRateIn): Promise<unknown> {
  return unwrap(addDeskAgentRateApiV1ProfitDeskAgentsDeskAgentIdRatesPost({ path: { desk_agent_id: deskAgentId }, body }))
}

export function patchDeskAgent(deskAgentId: string, body: DeskAgentPatch): Promise<DeskAgentOut> {
  return unwrap(patchDeskAgentApiV1ProfitDeskAgentsDeskAgentIdPatch({ path: { desk_agent_id: deskAgentId }, body }))
}
